import React, { useContext, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { AuthContext } from '../context/AuthContext';
import priviLogo from '../assets/Privi-Leads.png';
import mpLogo from '../assets/mercadopago.png';
import paypalLogo from '../assets/paypal.png';

// Precio Premium: 10 USD. ARS = equivalente según dólar oficial (actualizar cotización cuando cambie)
const PREMIUM_USD = 10;
const DOLAR_OFICIAL_ARS = 1450;
const PREMIUM_ARS = PREMIUM_USD * DOLAR_OFICIAL_ARS; // 14.500 ARS

export default function Premium() {
  const { user, plan, subscription } = useContext(AuthContext);
  const isPremium = plan === 'premium';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const startMP = async () => {
    if (!user) return;
    setError('');
    setLoading(true);
    try {
      const fn = httpsCallable(functions, 'createMercadoPagoPreapproval');
      const res = await fn({ email: user.email || null });
      const { approvalUrl } = res.data || {};
      if (approvalUrl) {
        window.location.href = approvalUrl;
      } else {
        throw new Error('No se recibió URL de aprobación');
      }
    } catch (e) {
      setError(e.message || 'Error iniciando suscripción');
    } finally {
      setLoading(false);
    }
  };

  const startPayPal = async () => {
    if (!user) return;
    setError('');
    setLoading(true);
    try {
      const fn = httpsCallable(functions, 'createPayPalSubscription');
      const res = await fn({});
      const { approvalUrl } = res.data || {};
      if (approvalUrl) {
        window.location.href = approvalUrl;
      } else {
        throw new Error('No se recibió URL de aprobación');
      }
    } catch (e) {
      setError(e.message || 'Error iniciando suscripción');
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async () => {
    // Advertencia sobre eliminación de contactos excedentes
    const warningMessage = `⚠️ IMPORTANTE: Al cancelar tu suscripción Premium:\n\n` +
      `✓ Mantendrás acceso Premium hasta el final del periodo pagado\n` +
      `✓ No se realizarán más cobros\n\n` +
      `⚠️ ATENCIÓN - ELIMINACIÓN DE CONTACTOS:\n` +
      `Si tienes más de 100 contactos, cuando expire tu periodo Premium se eliminarán AUTOMÁTICAMENTE los contactos MÁS RECIENTES que excedan los 100 permitidos en el plan gratuito. Se mantendrán tus 100 contactos más antiguos.\n\n` +
      `💡 RECOMENDACIÓN: Antes de cancelar, te sugerimos exportar todos tus contactos a CSV desde el Dashboard para conservar un respaldo completo.\n\n` +
      `¿Deseas continuar con la cancelación?`;

    if (!window.confirm(warningMessage)) return;

    setError('');
    setLoading(true);
    try {
      const fn = httpsCallable(functions, 'cancelSubscription');
      await fn({});
      alert('Suscripción cancelada. Mantendrás tu acceso Premium hasta el final del periodo actual.\n\nRecuerda exportar tus contactos antes de que expire tu periodo si tienes más de 100.');
      window.location.reload();
    } catch (e) {
      setError(e.message || 'Error cancelando suscripción');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="premium-container">
        <h2>Premium</h2>
        <p>Inicia sesión para suscribirte.</p>
      </div>
    );
  }

  return (
      <div className="premium-container">
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Procesando solicitud...</p>
        </div>
      )}
      <div className="premium-header">
        <img src={priviLogo} alt="Privilead Premium" className="premium-logo" />
        <h2 className="premium-title">⭐ Premium</h2>
        <p>Potencia tu negocio con acceso ilimitado a herramientas profesionales de gestión de contactos</p>
      </div>

      {/* Plan Comparison */}
      {!isPremium && (
        <div className="premium-compare">
          <h3>📊 Comparación de Planes</h3>
          <div className="premium-compare-grid">
            <div className="premium-plan-card">
              <h4>🆓 Plan Gratuito</h4>
              <ul>
                <li>✓ Hasta 100 contactos</li>
                <li>✓ Hasta 10 resultados por búsqueda</li>
                <li>✓ Gestión de agendas</li>
                <li style={{ color: 'var(--danger-color)' }}>✗ Sin analíticas</li>
                <li style={{ color: 'var(--danger-color)' }}>✗ Sin exportar a CSV</li>
                <li style={{ color: 'var(--danger-color)' }}>✗ Sin copiar emails</li>
              </ul>
            </div>
            <div className="premium-plan-card premium-plan-premium">
              <h4>⭐ Plan Premium</h4>
              <ul>
                <li>✓ Hasta 2,000 contactos</li>
                <li>✓ Hasta 50 resultados por búsqueda</li>
                <li>✓ Agendas ilimitadas</li>
                <li>✓ Acceso a Analytics</li>
                <li>✓ Exportar a CSV</li>
                <li>✓ Copiar emails masivamente</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {isPremium ? (
        <div className="premium-status active">
          <h3>🎉 ¡Eres usuario Premium!</h3>
          <div><strong>Estado:</strong> {subscription?.status === 'cancelled' ? '⚠️ Cancelado (Activo hasta fin de periodo)' : '✅ Activo'}</div>
          <div><strong>Búsquedas:</strong> hasta 50 resultados por búsqueda</div>
          {subscription?.provider && (
            <div><strong>Proveedor:</strong> {subscription.provider === 'mercadopago' ? 'MercadoPago' : 'PayPal'}</div>
          )}
          {subscription?.periodEndDate && (
            <div><strong>Próxima renovación:</strong> {new Date(subscription.periodEndDate.seconds * 1000).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
          )}
          {subscription?.status !== 'cancelled' && (
            <>
              <div className="premium-warning-box">
                ⚠️ <strong>Importante antes de cancelar:</strong> Si tienes más de 100 contactos, cuando expire tu periodo Premium se eliminarán automáticamente los más recientes que excedan el límite gratuito, manteniendo tus 100 contactos más antiguos. <strong>Te recomendamos exportar todos tus contactos a CSV desde el Dashboard antes de cancelar.</strong> Mantendrás tu acceso Premium hasta el final del periodo pagado y no se realizarán más cobros.
              </div>
              <button className="btn-cancel-subscription" onClick={cancelSubscription} disabled={loading}>
                {loading ? 'Cancelando...' : 'Cancelar suscripción'}
              </button>
            </>
          )}
          {subscription?.status === 'cancelled' && subscription?.willExpireAt && (
            <div className="premium-warning-box">
              ⏰ Tu acceso Premium finalizará el <strong>{new Date(subscription.willExpireAt.seconds * 1000).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>. Puedes renovar en cualquier momento antes de esa fecha.
            </div>
          )}
        </div>
      ) : (
        <>
          <h3>💳 Elige tu método de pago</h3>
          <div className="premium-info-box">
            ℹ️ <strong>Suscripción mensual automática.</strong> Puedes cancelar cuando quieras y seguirás teniendo acceso hasta el final del mes pagado.
          </div>
          <div className="premium-options">
            <div className="premium-option">
              <div className="premium-price">ARS ${PREMIUM_ARS.toLocaleString('es-AR')} / mes</div>
              <p>Pago en pesos (equiv. USD {PREMIUM_USD} al dólar oficial)</p>
              <button className="premium-btn mp" onClick={startMP} disabled={loading} aria-label="Mercado Pago">
                {loading ? <span>Cargando...</span> : <img src={mpLogo} alt="Mercado Pago" className="premium-pay-logo" />}
              </button>
            </div>
            <div className="premium-option">
              <div className="premium-price">USD ${PREMIUM_USD} / mes</div>
              <p>Pago en dólares (internacional)</p>
              <button className="premium-btn paypal" onClick={startPayPal} disabled={loading} aria-label="PayPal">
                {loading ? <span>Cargando...</span> : <img src={paypalLogo} alt="PayPal" className="premium-pay-logo" />}
              </button>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="premium-faq">
            <h3>❓ Preguntas Frecuentes</h3>
            <div>
              <div>
                <h4>¿Cuándo se realiza el cobro?</h4>
                <p>El primer cobro se realiza al momento de suscribirte. Luego se renueva automáticamente cada mes en la misma fecha.</p>
              </div>
              <div>
                <h4>¿Puedo cancelar cuando quiera?</h4>
                <p>Sí, puedes cancelar en cualquier momento. Mantendrás tu acceso Premium hasta el final del periodo pagado, sin cargos adicionales.</p>
              </div>
              <div>
                <h4>¿Qué pasa con mis contactos si cancelo?</h4>
                <p><strong>⚠️ IMPORTANTE:</strong> Si tienes más de 100 contactos cuando expire tu periodo Premium, se eliminarán automáticamente los contactos más recientes que excedan el límite de 100 del plan gratuito. Se mantendrán tus 100 contactos más antiguos (los más valiosos). <strong>Te recomendamos exportar todos tus contactos a CSV desde el Dashboard antes de cancelar para mantener un respaldo completo.</strong></p>
              </div>
              <div>
                <h4>¿Es seguro el pago?</h4>
                <p>Absolutamente. Usamos MercadoPago y PayPal, plataformas de pago líderes con encriptación de nivel bancario. No almacenamos tu información de pago.</p>
              </div>
            </div>
          </div>
        </>
      )}

      {error && <div className="premium-error">❌ {error}</div>}
    </div>
  );
}
